import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { ArrowLeft, Camera, Instagram, Facebook, Twitter, Globe, Loader2, LogOut, Trash2 } from 'lucide-react';
import { MobileShell } from '../components/MobileShell';
import { TopBar } from '../components/TopBar';

const CATEGORIES = [
  { id: 'yacht', label_en: 'Yachts', label_es: 'Yates' },
  { id: 'tour', label_en: 'Tours', label_es: 'Tours' },
  { id: 'activity', label_en: 'Activities', label_es: 'Actividades' },
  { id: 'party', label_en: 'Parties', label_es: 'Fiestas' },
  { id: 'car_rental', label_en: 'Car Rental', label_es: 'Renta de Autos' },
  { id: 'lodging', label_en: 'Lodging', label_es: 'Hospedaje' }
];

const LOCATIONS = [
  { id: 'playa_del_carmen', label: 'Playa del Carmen' },
  { id: 'cancun', label: 'Cancún' },
  { id: 'tulum', label: 'Tulum' },
  { id: 'quintana_roo', label: 'Quintana Roo' },
  { id: 'los_cabos', label: 'Los Cabos' }
];

export default function ProfilePage() {
  const navigate = useNavigate();
  const { user, api, token, logout, updateUser } = useAuth();
  const { language } = useLanguage();
  const fileInputRef = useRef(null);

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState(user?.profile_photo || null);
  const [deleting, setDeleting] = useState(false);

  const handleDeleteAccount = async () => {
    const confirmMsg = language === 'en'
      ? 'This will PERMANENTLY delete your BidTrip account, bookings, messages, products and all related data. This cannot be undone.\n\nType DELETE to confirm.'
      : 'Esto ELIMINARÁ PERMANENTEMENTE tu cuenta de BidTrip, reservas, mensajes, productos y todos los datos relacionados. No se puede deshacer.\n\nEscribe ELIMINAR para confirmar.';
    const expected = language === 'en' ? 'DELETE' : 'ELIMINAR';
    const answer = window.prompt(confirmMsg);
    if (!answer || answer.trim().toUpperCase() !== expected) {
      toast.info(language === 'en' ? 'Account deletion cancelled' : 'Eliminación cancelada');
      return;
    }
    setDeleting(true);
    try {
      await api.delete('/auth/account');
      toast.success(language === 'en' ? 'Account deleted. We will miss you.' : 'Cuenta eliminada. Te extrañaremos.');
      logout();
      navigate('/', { replace: true });
    } catch (err) {
      console.error('Delete account failed', err);
      toast.error(language === 'en' ? 'Could not delete account. Please try again.' : 'No se pudo eliminar la cuenta. Intenta de nuevo.');
    } finally {
      setDeleting(false);
    }
  };
  
  const [formData, setFormData] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    company_name: user?.company_name || '',
    description: user?.description || '',
    description_en: user?.description_en || '',
    categories: user?.categories || [],
    locations: user?.locations || [],
    social_links: user?.social_links || {},
    auto_translate_chat: user?.auto_translate_chat ?? false,
    preferred_lang: user?.preferred_lang || language || 'es',
  });

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error(language === 'en' ? 'Please select an image' : 'Selecciona una imagen');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await api().post('/upload/profile-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setProfilePhoto(response.data.path);
      toast.success(language === 'en' ? 'Photo uploaded!' : '¡Foto subida!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error uploading photo');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await api().put('/auth/profile', formData);
      updateUser({
        auto_translate_chat: formData.auto_translate_chat,
        preferred_lang: formData.preferred_lang,
        name: formData.name,
        phone: formData.phone,
        company_name: formData.company_name,
        description: formData.description,
        description_en: formData.description_en,
        categories: formData.categories,
        locations: formData.locations,
        social_links: formData.social_links,
      });
      toast.success(language === 'en' ? 'Profile updated!' : '¡Perfil actualizado!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error updating profile');
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (catId) => {
    setFormData(prev => ({
      ...prev,
      categories: prev.categories.includes(catId)
        ? prev.categories.filter(c => c !== catId)
        : [...prev.categories, catId]
    }));
  };

  const toggleLocation = (locId) => {
    setFormData(prev => ({
      ...prev,
      locations: prev.locations.includes(locId)
        ? prev.locations.filter(l => l !== locId)
        : [...prev.locations, locId]
    }));
  };

  const updateSocialLink = (platform, value) => {
    setFormData(prev => ({
      ...prev,
      social_links: { ...prev.social_links, [platform]: value }
    }));
  };

  const getPhotoUrl = () => {
    if (!profilePhoto) return null;
    return `${process.env.REACT_APP_BACKEND_URL}/api/files/${profilePhoto}?auth=${token}`;
  };

  return (
    <MobileShell>
      <TopBar
        showBrand={false}
        title={language === 'en' ? 'Profile' : 'Perfil'}
        right={
          <button onClick={() => navigate('/dashboard')} className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted">
            <ArrowLeft size={18} />
          </button>
        }
      />
      <div className="px-4 py-4" data-testid="profile-page">
        <div className="bg-card border border-border rounded-2xl p-5">
          <h1 className="font-heading text-xl font-bold mb-4">
            {language === 'en' ? 'Edit Profile' : 'Editar Perfil'}
          </h1>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Profile Photo */}
            <div className="flex flex-col items-center">
              <div className="relative">
                <div className="w-24 h-24 rounded-full bg-caribbean/10 flex items-center justify-center overflow-hidden border-4 border-caribbean/20">
                  {profilePhoto ? (
                    <img
                      src={getPhotoUrl()}
                      alt="Profile"
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <span className="font-heading text-3xl text-caribbean font-bold">
                      {user?.name?.charAt(0).toUpperCase()}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="absolute bottom-0 right-0 w-8 h-8 bg-caribbean rounded-full flex items-center justify-center text-white shadow-lg hover:bg-caribbean/90 transition-colors"
                >
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <Camera size={16} />}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                />
              </div>
              <p className="text-sm text-muted-foreground mt-2">
                {language === 'en' ? 'Tap to change photo' : 'Toca para cambiar foto'}
              </p>
            </div>

            {/* Basic Info */}
            <div className="space-y-2">
              <Label>{language === 'en' ? 'Full Name' : 'Nombre Completo'}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="h-12"
              />
            </div>

            <div className="space-y-2">
              <Label>{language === 'en' ? 'Phone' : 'Teléfono'}</Label>
              <Input
                type="tel"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="h-12"
              />
            </div>

            {/* Provider-specific fields */}
            {user?.role === 'provider' && (
              <>
                <div className="space-y-2">
                  <Label>{language === 'en' ? 'Company Name' : 'Nombre de Empresa'}</Label>
                  <Input
                    value={formData.company_name}
                    onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                    className="h-12"
                  />
                </div>

                <div className="space-y-2">
                  <Label>
                    {language === 'en' ? 'Description (Spanish) 🇲🇽' : 'Descripción (Español) 🇲🇽'}
                  </Label>
                  <Textarea
                    data-testid="profile-description-es"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder={language === 'en' ? 'Tell us about your services in Spanish...' : 'Cuéntanos sobre tus servicios en español...'}
                    className="min-h-[100px]"
                  />
                </div>

                <div className="space-y-2">
                  <Label>
                    {language === 'en' ? 'Description (English) 🇺🇸' : 'Descripción (Inglés) 🇺🇸'}
                  </Label>
                  <Textarea
                    data-testid="profile-description-en"
                    value={formData.description_en}
                    onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                    placeholder={language === 'en' ? 'Tell us about your services in English...' : 'Cuéntanos sobre tus servicios en inglés...'}
                    className="min-h-[100px]"
                  />
                  <p className="text-[10px] text-muted-foreground">
                    {language === 'en'
                      ? 'Tourists will see the description in their selected language.'
                      : 'Los turistas verán la descripción en su idioma seleccionado.'}
                  </p>
                </div>

                {/* Categories */}
                <div className="space-y-2">
                  <Label>{language === 'en' ? 'Categories' : 'Categorías'}</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {CATEGORIES.map(cat => (
                      <div key={cat.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`cat-${cat.id}`}
                          checked={formData.categories.includes(cat.id)}
                          onCheckedChange={() => toggleCategory(cat.id)}
                        />
                        <label htmlFor={`cat-${cat.id}`} className="text-sm cursor-pointer">
                          {language === 'es' ? cat.label_es : cat.label_en}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Locations */}
                <div className="space-y-2">
                  <Label>{language === 'en' ? 'Locations' : 'Ubicaciones'}</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {LOCATIONS.map(loc => (
                      <div key={loc.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`loc-${loc.id}`}
                          checked={formData.locations.includes(loc.id)}
                          onCheckedChange={() => toggleLocation(loc.id)}
                        />
                        <label htmlFor={`loc-${loc.id}`} className="text-sm cursor-pointer">
                          {loc.label}
                        </label>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* Social Links */}
            <div className="space-y-4">
              <Label>{language === 'en' ? 'Social Links' : 'Redes Sociales'}</Label>
              
              <div className="flex items-center gap-3">
                <Instagram className="text-rosa" size={20} />
                <Input
                  placeholder="instagram.com/username"
                  value={formData.social_links?.instagram || ''}
                  onChange={(e) => updateSocialLink('instagram', e.target.value)}
                  className="h-10"
                />
              </div>

              <div className="flex items-center gap-3">
                <Facebook className="text-blue-600" size={20} />
                <Input
                  placeholder="facebook.com/username"
                  value={formData.social_links?.facebook || ''}
                  onChange={(e) => updateSocialLink('facebook', e.target.value)}
                  className="h-10"
                />
              </div>

              <div className="flex items-center gap-3">
                <Twitter className="text-sky-500" size={20} />
                <Input
                  placeholder="twitter.com/username"
                  value={formData.social_links?.twitter || ''}
                  onChange={(e) => updateSocialLink('twitter', e.target.value)}
                  className="h-10"
                />
              </div>

              <div className="flex items-center gap-3">
                <Globe className="text-caribbean" size={20} />
                <Input
                  placeholder="yourwebsite.com"
                  value={formData.social_links?.website || ''}
                  onChange={(e) => updateSocialLink('website', e.target.value)}
                  className="h-10"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 rounded-full bg-caribbean hover:bg-caribbean/90 text-white font-semibold"
            >
              {loading ? (language === 'en' ? 'Saving...' : 'Guardando...') : (language === 'en' ? 'Save Profile' : 'Guardar Perfil')}
            </Button>
          </form>
        </div>

        <button
          type="button"
          onClick={() => { logout(); navigate('/'); }}
          data-testid="logout-btn"
          className="mt-4 w-full flex items-center justify-center gap-2 h-12 rounded-2xl bg-card border border-border text-destructive font-semibold active:scale-[0.98] transition-transform"
        >
          <LogOut size={18} />
          {language === 'en' ? 'Log out' : 'Cerrar sesión'}
        </button>

        <button
          type="button"
          onClick={handleDeleteAccount}
          disabled={deleting}
          data-testid="delete-account-btn"
          className="mt-3 w-full flex items-center justify-center gap-2 h-12 rounded-2xl bg-destructive/10 border border-destructive/40 text-destructive font-semibold active:scale-[0.98] transition-transform disabled:opacity-60"
        >
          {deleting ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
          {deleting
            ? (language === 'en' ? 'Deleting account...' : 'Eliminando cuenta...')
            : (language === 'en' ? 'Delete my account' : 'Eliminar mi cuenta')}
        </button>
        <p className="mt-2 text-xs text-muted-foreground text-center px-4">
          {language === 'en'
            ? 'Permanently removes your account and all related data. This cannot be undone.'
            : 'Elimina permanentemente tu cuenta y todos los datos relacionados. No se puede deshacer.'}
        </p>
      </div>
    </MobileShell>
  );
}
